import { IModeSwitchPending } from 'utils/interfaces'

import { modeSwitchConfirmWaitMs } from './modeSwitchConfirmation'

/** Celery can queue robot_mode_switch for >10s before MQTT leaves. */
export const NTRIP_APPLY_CONFIRM_FALLBACK_MS = 30_000

/** After pending clears, wait a bit for telemetry.ntrip to catch up. */
export const NTRIP_TELEMETRY_GRACE_MS = 5_000

export const NTRIP_MOUNTPOINT_AUTO = 'AUTO'

/** Hostnames and mountpoints must be ASCII (no Cyrillic / other non-Latin scripts). */
const NTRIP_ASCII_TOKEN = /^[\x21-\x7E]+$/

export type NtripSource = {
  caster?: string
  port?: number | null
  user?: string
  mountpoint?: string
}

/** Robot reports connection health; compare case-insensitively. */
export function isNtripConnected(status?: string | null): boolean {
  return String(status ?? '').trim().toLowerCase() === 'connected'
}

export type NtripFormValues = {
  caster: string
  port: string
  user: string
  password: string
  mountpoint: string
  passwordSet: boolean
}

export function ntripSourcesMatch(expected: NtripSource, actual: NtripSource | null | undefined): boolean {
  if (!actual || expected.port == null || actual.port == null) {
    return false
  }
  if (Number(actual.port) !== Number(expected.port)) {
    return false
  }
  return (
    String(actual.caster ?? '') === String(expected.caster ?? '') &&
    String(actual.user ?? '') === String(expected.user ?? '') &&
    (actual.mountpoint || NTRIP_MOUNTPOINT_AUTO) === (expected.mountpoint || NTRIP_MOUNTPOINT_AUTO)
  )
}

export function validateNtripForm(form: NtripFormValues): string | null {
  const caster = form.caster.trim()
  if (!caster || caster.length > 255 || /\s/.test(caster)) {
    return 'Caster hostname is required'
  }
  if (!NTRIP_ASCII_TOKEN.test(caster)) {
    return 'Caster must use Latin characters only'
  }
  const port = Number(form.port)
  if (!Number.isInteger(port) || port < 1 || port > 65535) {
    return 'Port must be an integer from 1 to 65535'
  }
  const user = form.user.trim()
  if (!user || user.length > 128) {
    return 'User is required'
  }
  const mountpoint = form.mountpoint.trim() || NTRIP_MOUNTPOINT_AUTO
  if (mountpoint.length > 128) {
    return 'Mountpoint is too long'
  }
  if (!NTRIP_ASCII_TOKEN.test(mountpoint)) {
    return 'Mountpoint must use Latin characters only'
  }
  if (form.password.length > 256) {
    return 'Password is too long'
  }
  if (!form.passwordSet && !form.password.trim()) {
    return 'Password is required'
  }
  return null
}

export function ntripSaveBody(form: NtripFormValues): {
  caster: string
  port: number
  user: string
  mountpoint: string
  password?: string
} {
  const body = {
    caster: form.caster.trim(),
    port: Number(form.port),
    user: form.user.trim(),
    mountpoint: form.mountpoint.trim() || NTRIP_MOUNTPOINT_AUTO,
  }
  const password = form.password.trim()
  if (password) {
    return { ...body, password }
  }
  return body
}

/**
 * Resolves when telemetry or mode feedback carries the same caster, port, user,
 * and mountpoint that were just saved. Mode 103 itself is transient.
 *
 * Waits for mode-switch pending when Celery delays MQTT, then keeps a short
 * grace window after pending clears so telemetry can update.
 */
export function waitForNtripApplyConfirmation(
  expected: NtripSource,
  read: () => {
    telemetry?: NtripSource | null
    feedback?: NtripSource | null
    pending?: Pick<IModeSwitchPending, 'requestedAt' | 'timeoutSec'> | null
  },
  options?: {
    fallbackMs?: number
    graceMs?: number
    intervalMs?: number
    isAborted?: () => boolean
  }
): Promise<'confirmed' | 'timeout' | 'aborted'> {
  const fallbackMs = options?.fallbackMs ?? NTRIP_APPLY_CONFIRM_FALLBACK_MS
  const graceMs = options?.graceMs ?? NTRIP_TELEMETRY_GRACE_MS
  const intervalMs = options?.intervalMs ?? 200
  const isAborted = options?.isAborted ?? (() => false)
  return new Promise(resolve => {
    const started = Date.now()
    let sawPending = Boolean(read().pending)
    let pendingClearedAt: number | null = null
    const tick = () => {
      if (isAborted()) {
        resolve('aborted')
        return
      }
      const snapshot = read()
      if (ntripSourcesMatch(expected, snapshot.telemetry) || ntripSourcesMatch(expected, snapshot.feedback)) {
        resolve('confirmed')
        return
      }
      if (snapshot.pending) {
        sawPending = true
        pendingClearedAt = null
      } else if (sawPending && pendingClearedAt == null) {
        pendingClearedAt = Date.now()
      }
      if (pendingClearedAt != null && Date.now() - pendingClearedAt >= graceMs) {
        resolve('timeout')
        return
      }
      if (!sawPending && Date.now() - started >= fallbackMs) {
        resolve('timeout')
        return
      }
      if (snapshot.pending && modeSwitchConfirmWaitMs(snapshot.pending) <= 0 && pendingClearedAt == null) {
        pendingClearedAt = Date.now()
      }
      window.setTimeout(tick, intervalMs)
    }
    tick()
  })
}
